import React, { useEffect, useId, useRef } from 'react';
import ReactDOM from 'react-dom';
import useFocusTrap from '../../hooks/useFocusTrap';
import './Drawer.css';

/**
 * Drawer — right-anchored slide-over panel rendered via React Portal.
 * On mobile (<768px) it transforms into a bottom sheet.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - title: string
 *  - children: ReactNode
 *  - width: string (default '420px') — width on desktop only
 *  - side: 'right' (default; only right supported for now)
 *  - initialFocusRef: optional ref
 */
export default function Drawer({
  open,
  onClose,
  title,
  children,
  width = '420px',
  side = 'right',
  initialFocusRef,
}) {
  const overlayRef = useRef(null);
  const panelRef = useRef(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose && onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  useFocusTrap(panelRef, { active: !!open, initialFocusRef });

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose && onClose();
  };

  return ReactDOM.createPortal(
    <div
      ref={overlayRef}
      className={`ui-drawer__overlay ui-drawer__overlay--${side}`}
      onClick={handleOverlayClick}
    >
      <aside
        ref={panelRef}
        className={`ui-drawer__panel ui-drawer__panel--${side}`}
        style={{ '--ui-drawer-width': width }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="ui-drawer__header">
          <h2 id={titleId} className="ui-drawer__title">{title}</h2>
          <button
            type="button"
            className="ui-drawer__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <div className="ui-drawer__body">{children}</div>
      </aside>
    </div>,
    document.body
  );
}
